import axios, { AxiosInstance, AxiosError } from 'axios';
import type {
  DadosVendas,
  DadosPagamento,
  DadosProduto,
  DadosCaixa,
  RespostaApiVendas,
  RespostaApiPagamento,
  RespostaApiPagamentoPeriodo,
  RespostaApiProduto,
  RespostaApiProdutoPeriodo,
  RespostaApiCaixa,
  RespostaApiHealth,
  ModuloOperacional,
} from '../tipos';

/**
 * Serviço de API HTTP com Axios
 * Requisitos: 1, 2, 3, 4, 5, 8, 16, 18
 *
 * - Configura Axios com baseURL e timeout de 10 segundos
 * - Interceptor de requisição: adiciona token JWT aos headers
 * - Interceptor de resposta: trata erros 401, 429, 503
 * - Métodos de consulta alternam entre API local (estaOnline=true)
 *   e API central PostgreSQL (estaOnline=false)
 */

const TIMEOUT_MS = 10_000;

// URL base da API central (VPS com PostgreSQL)
const URL_API_CENTRAL = import.meta.env.VITE_API_CENTRAL_URL || '';
const CHAVE_TOKEN_CENTRAL = 'nexo_token';
const CHAVE_EXPIRACAO_TOKEN_CENTRAL = 'nexo_token_expiracao';

/**
 * Cria uma instância Axios configurada com interceptors
 */
const criarClienteHttp = (
  baseURL: string,
  token?: string | null,
  limparSessaoCentralAo401 = true,
  aoNaoAutorizado?: () => void
): AxiosInstance => {
  const cliente = axios.create({
    baseURL,
    timeout: TIMEOUT_MS,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Interceptor de requisição: adiciona token JWT ao header Authorization
  cliente.interceptors.request.use(
    (config) => {
      const tokenRequisicao =
        token === undefined ? localStorage.getItem(CHAVE_TOKEN_CENTRAL) : token;
      if (tokenRequisicao) {
        config.headers.Authorization = `Bearer ${tokenRequisicao}`;
      }
      return config;
    },
    (error) => Promise.reject(error)
  );

  // Interceptor de resposta: trata erros comuns (401, 429, 503)
  cliente.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => {
      if (error.response?.status === 401) {
        // Token expirado ou inválido - limpa sessão e notifica a aplicação
        aoNaoAutorizado?.();
        if (limparSessaoCentralAo401) {
          localStorage.removeItem(CHAVE_TOKEN_CENTRAL);
          localStorage.removeItem(CHAVE_EXPIRACAO_TOKEN_CENTRAL);
          window.dispatchEvent(new CustomEvent('nexo:sessao-expirada'));
        }
      } else if (error.response?.status === 429) {
        // Rate limit excedido - notifica com informação de retry
        const retryAfter = error.response.headers['retry-after'];
        window.dispatchEvent(
          new CustomEvent('nexo:rate-limit', { detail: { retryAfter } })
        );
      } else if (error.response?.status === 503) {
        // Servidor indisponível - notifica para ativar modo offline
        window.dispatchEvent(new CustomEvent('nexo:servidor-indisponivel'));
      }
      return Promise.reject(error);
    }
  );

  return cliente;
};

// ============================================================
// Mapeadores de resposta da API para tipos internos
// ============================================================

const mapearVendas = (resposta: RespostaApiVendas): DadosVendas => {
  // Proteção contra resposta nula ou formato inesperado
  if (!resposta || typeof resposta !== 'object' || Array.isArray(resposta)) {
    return {
      data: new Date().toISOString().split('T')[0],
      valorTotal: 0,
      contagemTransacoes: 0,
      porModulo: { mesa: 0, balcao: 0, entrega: 0 },
    };
  }

  const porModulo = { mesa: 0, balcao: 0, entrega: 0 };

  if (Array.isArray(resposta.by_module)) {
    // Formato array: [{"module": "balcao", "total_amount": 32.12}]
    for (const item of resposta.by_module) {
      const mod = item.module as keyof typeof porModulo;
      if (mod in porModulo) {
        porModulo[mod] = item.total_amount || 0;
      }
    }
  } else if (resposta.by_module && typeof resposta.by_module === 'object') {
    // Formato objeto: {"mesa": 0, "balcao": 32.12, "entrega": 0}
    porModulo.mesa = resposta.by_module.mesa || 0;
    porModulo.balcao = resposta.by_module.balcao || 0;
    porModulo.entrega = resposta.by_module.entrega || 0;
  }

  const totalAmount = resposta.total_amount ?? 0;
  const transactionCount = resposta.transaction_count ?? 0;

  // Se nenhum módulo tem valor mas total_amount > 0, atribui tudo a balcao
  if (porModulo.mesa + porModulo.balcao + porModulo.entrega === 0 && totalAmount > 0) {
    porModulo.balcao = totalAmount;
  }

  return {
    data: resposta.date || new Date().toISOString().split('T')[0],
    valorTotal: totalAmount,
    contagemTransacoes: transactionCount,
    porModulo,
  };
};

const mapearPagamento = (resposta: RespostaApiPagamento): DadosPagamento => ({
  tipoPagamento: resposta.payment_type,
  nomeTipoPagamento: resposta.payment_name || formatarNomeTipoPagamento(resposta.payment_type),
  valor: resposta.amount,
  contagem: resposta.count,
});

/**
 * Converte o código do tipo de pagamento em nome legível.
 */
const formatarNomeTipoPagamento = (tipo: string): string => {
  const mapa: Record<string, string> = {
    dinheiro: 'Dinheiro',
    cartao: 'Cartão',
    credito: 'Cartão de Crédito',
    debito: 'Cartão de Débito',
    pix: 'PIX',
    tickets: 'Tickets',
    cheque: 'PIX',
    convenio: 'Convênio',
    pendura: 'Pendura',
    funcionario: 'Funcionário',
    cortesia: 'Cortesia',
    voucher: 'Voucher',
    transferencia: 'Transferência',
  };
  return mapa[tipo.toLowerCase()] || tipo;
};

const mapearProduto = (resposta: RespostaApiProduto): DadosProduto => ({
  nomeProduto: resposta.product_name,
  quantidade: resposta.quantity,
  receita: resposta.revenue,
  modulo: resposta.module as ModuloOperacional,
});

const mapearCaixa = (resposta: RespostaApiCaixa): DadosCaixa => ({
  idCaixa: resposta.cashier_id,
  numeroCaixa: resposta.cashier_number,
  status: resposta.status,
  aberturaEm: resposta.opened_at,
  fechamentoEm: resposta.closed_at,
  modulo: (resposta.module || 'mesa') as ModuloOperacional,
  operadorAbertura: resposta.operator || resposta.operator_open || '',
  operadorFechamento: resposta.operator_close || '',
  saldoAtual: resposta.current_balance,
  valorAbertura: resposta.opening_balance ?? undefined,
  valorFechamento: resposta.closing_balance ?? undefined,
  valorApurado: resposta.calculated_closing_balance ?? undefined,
  valorDigitado: resposta.entered_closing_balance ?? undefined,
  diferenca: resposta.closing_difference ?? undefined,
  temDiferenca: resposta.has_closing_difference ?? false,
});

const vendasSemDados = (dados: DadosVendas): boolean => {
  const totalModulos =
    dados.porModulo.mesa + dados.porModulo.balcao + dados.porModulo.entrega;

  return dados.valorTotal === 0 && dados.contagemTransacoes === 0 && totalModulos === 0;
};

// ============================================================
// Serviço de API
// ============================================================

class ServicoApi {
  /**
   * Retorna o cliente HTTP configurado para a fonte de dados correta.
   * - estaOnline=true: usa proxy via API central no formato /api/proxy/local/{idEstabelecimento}/{endpoint}
   * - estaOnline=false: usa a API central (PostgreSQL na VPS)
   */
  private obterCliente(
    idEstabelecimento: string,
    _urlBaseLocal: string,
    estaOnline: boolean
  ): AxiosInstance {
    if (!estaOnline) {
      return criarClienteHttp(URL_API_CENTRAL);
    }

    // Usa proxy via API central — token central (localStorage) é usado para autenticação
    const baseURL = `${URL_API_CENTRAL}/api/proxy/local/${idEstabelecimento}`;
    return criarClienteHttp(baseURL);
  }

  /**
   * Obtém vendas diárias do estabelecimento.
   * Quando online, consulta a API local Horse/Delphi.
   * Quando offline, consulta a API central PostgreSQL.
   */
  async obterVendasDiarias(
    idEstabelecimento: string,
    data: string,
    estaOnline: boolean,
    urlBaseLocal?: string,
    dataFim?: string,
    idCaixa?: number
  ): Promise<DadosVendas> {
    const cliente = this.obterCliente(idEstabelecimento, urlBaseLocal || '', estaOnline);

    const endpoint = estaOnline
      ? '/api/v1/sales/daily'
      : '/api/central/sales/daily';

    const params: Record<string, string | number> = { date: data };
    if (dataFim) {
      params.date_end = dataFim;
    }
    if (idCaixa) {
      params.cashier_id = idCaixa;
    }
    if (!estaOnline) {
      params.establishment_id = idEstabelecimento;
    }

    try {
      const resposta = await cliente.get<RespostaApiVendas>(endpoint, { params });
      const dados = mapearVendas(resposta.data);

      if (estaOnline && vendasSemDados(dados)) {
        return this.obterVendasDiarias(
          idEstabelecimento,
          data,
          false,
          urlBaseLocal,
          dataFim,
          idCaixa
        );
      }

      return dados;
    } catch (erro) {
      if (estaOnline) {
        return this.obterVendasDiarias(
          idEstabelecimento,
          data,
          false,
          urlBaseLocal,
          dataFim,
          idCaixa
        );
      }
      throw erro;
    }
  }

  /**
   * Obtém detalhamento de pagamentos por tipo.
   * Quando online e sem intervalo, consulta a API local Horse/Delphi.
   * Quando offline ou com intervalo de datas, consulta a API central PostgreSQL.
   */
  async obterDetalhamentoPagamentos(
    idEstabelecimento: string,
    data: string,
    estaOnline: boolean,
    urlBaseLocal?: string,
    dataFim?: string,
    idCaixa?: number
  ): Promise<DadosPagamento[]> {
    const cliente = this.obterCliente(idEstabelecimento, urlBaseLocal || '', estaOnline);

    const endpoint = estaOnline
      ? '/api/v1/payments/breakdown'
      : '/api/central/payments/breakdown';

    const params: Record<string, string | number> = { date: data };
    if (dataFim) {
      params.date_end = dataFim;
    }
    if (idCaixa) {
      params.cashier_id = idCaixa;
    }
    if (!estaOnline) {
      params.establishment_id = idEstabelecimento;
    }

    try {
      const resposta = await cliente.get<RespostaApiPagamento[] | RespostaApiPagamentoPeriodo>(endpoint, { params });
      const dados = Array.isArray(resposta.data) ? resposta.data : resposta.data.summary;

      if (estaOnline && dados.length === 0) {
        return this.obterDetalhamentoPagamentos(
          idEstabelecimento,
          data,
          false,
          urlBaseLocal,
          dataFim,
          idCaixa
        );
      }

      return dados.map(mapearPagamento);
    } catch (erro) {
      if (estaOnline) {
        return this.obterDetalhamentoPagamentos(
          idEstabelecimento,
          data,
          false,
          urlBaseLocal,
          dataFim,
          idCaixa
        );
      }
      throw erro;
    }
  }

  /**
   * Obtém os produtos mais vendidos.
   * Quando online e sem intervalo, consulta a API local Horse/Delphi.
   * Quando offline ou com intervalo de datas, consulta a API central PostgreSQL.
   */
  async obterProdutosMaisVendidos(
    idEstabelecimento: string,
    data: string,
    estaOnline: boolean,
    urlBaseLocal?: string,
    limite = 5,
    dataFim?: string
  ): Promise<DadosProduto[]> {
    const cliente = this.obterCliente(idEstabelecimento, urlBaseLocal || '', estaOnline);

    const endpoint = estaOnline
      ? '/api/v1/products/top'
      : '/api/central/products/top';

    const params: Record<string, string | number> = { date: data, limit: limite };
    if (dataFim) {
      params.date_end = dataFim;
    }
    if (!estaOnline) {
      params.establishment_id = idEstabelecimento;
    }

    try {
      const resposta = await cliente.get<RespostaApiProduto[] | RespostaApiProdutoPeriodo>(endpoint, { params });
      const dados = Array.isArray(resposta.data) ? resposta.data : resposta.data.summary;

      if (estaOnline && dados.length === 0) {
        return this.obterProdutosMaisVendidos(
          idEstabelecimento,
          data,
          false,
          urlBaseLocal,
          limite,
          dataFim
        );
      }

      return dados.map(mapearProduto);
    } catch (erro) {
      if (estaOnline) {
        return this.obterProdutosMaisVendidos(
          idEstabelecimento,
          data,
          false,
          urlBaseLocal,
          limite,
          dataFim
        );
      }
      throw erro;
    }
  }

  /**
   * Obtém o status dos caixas (abertos e fechados).
   * Quando online, consulta a API local Horse/Delphi.
   * Quando offline, consulta a API central PostgreSQL.
   */
  async obterStatusCaixas(
    idEstabelecimento: string,
    estaOnline: boolean,
    urlBaseLocal?: string
  ): Promise<DadosCaixa[]> {
    const cliente = this.obterCliente(idEstabelecimento, urlBaseLocal || '', estaOnline);

    const endpoint = estaOnline
      ? '/api/v1/cashiers/status'
      : '/api/central/cashiers/status';

    const params: Record<string, string> = {};
    if (!estaOnline) {
      params.establishment_id = idEstabelecimento;
    }

    try {
      const resposta = await cliente.get<RespostaApiCaixa[]>(endpoint, { params });

      if (estaOnline && resposta.data.length === 0) {
        return this.obterStatusCaixas(idEstabelecimento, false, urlBaseLocal);
      }

      return resposta.data.map(mapearCaixa);
    } catch (erro) {
      if (estaOnline) {
        return this.obterStatusCaixas(idEstabelecimento, false, urlBaseLocal);
      }
      throw erro;
    }
  }

  /**
   * Verifica a saúde do servidor local (endpoint /health).
   * Usado pelo serviço de sincronização para determinar se o servidor está online.
   */
  async verificarSaude(urlBase: string): Promise<RespostaApiHealth> {
    const cliente = criarClienteHttp(urlBase, null, false);
    const resposta = await cliente.get<RespostaApiHealth>('/api/v1/health', {
      timeout: 5_000, // timeout reduzido para health check
    });
    return resposta.data;
  }
}

export const servicoApi = new ServicoApi();
